import { DEMO_CONFIG } from "./demo.config";
import { DemoWalletService } from "./demo-wallet.service";

export type DemoRoundStage = "WAITING" | "RUNNING" | "CRASHED" | "STOPPED";

export interface DemoAction {
  action?: string;
  round?: string | number;
  stake?: number;
  betType?: string;
  betId?: string;
  betid?: string;
}

export interface DemoSocketMessage {
  type?: string;
  status?: string;
  action?: string;
  p?: string;
  r?: number;
  rv?: number;
  cv?: number;
  tl?: number;
  tcu?: number;
  balance?: number;
  message?: string;
  betId?: string;
  betid?: string;
  betType?: string;
  round?: number;
  stake?: number;
  multiplier?: number;
  RUNValue?: number;
  cashOut?: number;
  cashout?: number;
  nation?: string;
  bets?: DemoBetPayload[];
}

export interface DemoBetPayload {
  betId: string;
  clientName: string;
  stake: number;
  round: number;
  betType: string;
  createdAt: string;
  nation: "PLACED" | "CASHOUT";
  oddsRate?: number;
  cashOut?: number;
}

export interface DemoRoundResult {
  roundId: string;
  multiplier: string;
  createdAt: string;
  provablyFair: {
    clientSeed: Array<{ seed: string; name: string; _id: string }>;
    serverSeedHash: string;
    serverSeed: string;
    crashPoint: string;
    proof: string;
  };
}

interface ActiveDemoBet {
  betId: string;
  betType: string;
  stake: number;
  round: number;
  createdAt: string;
}

type MessageListener = (message: DemoSocketMessage) => void;

const randomBetween = (min: number, max: number) =>
  min + Math.random() * (max - min);

const generateCrashPoint = (): number => {
  const roll = Math.random();
  if (roll < 0.55) return Number(randomBetween(1.05, 2).toFixed(2));
  if (roll < 0.85) return Number(randomBetween(2, 5).toFixed(2));
  if (roll < 0.97) return Number(randomBetween(5, 15).toFixed(2));
  return Number(randomBetween(15, 50).toFixed(2));
};

const randomDemoMultiplier = () => `${generateCrashPoint().toFixed(2)}`;

export class DemoRoundEngineService {
  private running = false;
  private stage: DemoRoundStage = "STOPPED";
  private currentRoundId = Date.now();
  private currentMultiplier = 1;
  private crashPoint = 1;
  private betSequence = 0;
  private readonly listeners = new Set<MessageListener>();
  private readonly activeBets = new Map<string, ActiveDemoBet>();
  private readonly timers = new Set<ReturnType<typeof setTimeout>>();
  private waitingInterval: ReturnType<typeof setInterval> | null = null;
  private multiplierInterval: ReturnType<typeof setInterval> | null = null;
  private history: DemoRoundResult[] = [];

  constructor(private readonly wallet: DemoWalletService) {
    this.seedHistory();
  }

  getStage(): DemoRoundStage {
    return this.stage;
  }

  getRoundId(): number {
    return this.currentRoundId;
  }

  getMultiplier(): number {
    return this.currentMultiplier;
  }

  getHistory(): DemoRoundResult[] {
    return this.history.map((round) => ({
      ...round,
      provablyFair: {
        ...round.provablyFair,
        clientSeed: round.provablyFair.clientSeed.map((seed) => ({ ...seed })),
      },
    }));
  }

  subscribe(listener: MessageListener): () => void {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  }

  start(): void {
    if (this.running) return;

    this.running = true;
    this.debug("engine connected");
    this.emit({ type: "CONNECTED", status: "success" });
    this.emit({ type: "balance_update", balance: this.wallet.getBalance() });
    this.beginWaitingRound();
  }

  stop(): void {
    this.running = false;
    this.stage = "STOPPED";
    this.clearTimers();
    this.activeBets.clear();
    this.debug("engine stopped");
  }

  handleActions(message: DemoAction | DemoAction[]): void {
    const actions = Array.isArray(message) ? message : [message];
    actions.forEach((action) => {
      switch (action.action) {
        case "PlaceBet":
          this.placeBet(action);
          break;
        case "CancelBet":
          this.cancelBet(action);
          break;
        case "CashoutBet":
          this.cashOut(action);
          break;
        default:
          break;
      }
    });
  }

  private beginWaitingRound(): void {
    if (!this.running) return;

    this.currentRoundId += 1;
    this.currentMultiplier = 1;
    this.crashPoint = generateCrashPoint();
    this.stage = "WAITING";
    const startedAt = Date.now();

    const emitWaiting = () => {
      const elapsed = Date.now() - startedAt;
      const remainingMs = Math.max(0, DEMO_CONFIG.waitingDurationMs - elapsed);
      this.emit({
        p: "WAIT",
        r: this.currentRoundId,
        tl: Number((remainingMs / 1000).toFixed(2)),
      });
    };

    this.debug("round waiting", {
      round: this.currentRoundId,
      crashPoint: this.crashPoint,
    });
    emitWaiting();
    this.waitingInterval = setInterval(() => {
      emitWaiting();
      if (Date.now() - startedAt >= DEMO_CONFIG.waitingDurationMs) {
        if (this.waitingInterval) clearInterval(this.waitingInterval);
        this.waitingInterval = null;
        this.beginRunningRound();
      }
    }, 250);
  }

  private beginRunningRound(): void {
    if (!this.running) return;

    this.stage = "RUNNING";
    const startedAt = Date.now();
    this.emit({ p: "RUN", r: this.currentRoundId });
    this.debug("round started", { round: this.currentRoundId });

    this.multiplierInterval = setInterval(() => {
      const elapsed = Date.now() - startedAt;
      this.currentMultiplier = Number(Math.exp(elapsed / 8_000).toFixed(2));

      if (this.currentMultiplier >= this.crashPoint) {
        this.currentMultiplier = this.crashPoint;
        this.crashRound();
        return;
      }

      this.emit({ rv: this.currentMultiplier, r: this.currentRoundId });
    }, DEMO_CONFIG.multiplierTickMs);
  }

  private crashRound(): void {
    if (this.stage !== "RUNNING") return;

    if (this.multiplierInterval) clearInterval(this.multiplierInterval);
    this.multiplierInterval = null;
    this.stage = "CRASHED";

    this.activeBets.clear();
    const result = this.createRoundResult(
      this.currentRoundId,
      this.currentMultiplier,
      new Date().toISOString(),
    );
    this.history = [result, ...this.history].slice(0, 50);
    this.emit({
      p: "CRASH",
      cv: this.currentMultiplier,
      r: this.currentRoundId,
    });
    this.emit({
      type: "HISTORY_UPDATED",
      cv: this.currentMultiplier,
      r: this.currentRoundId,
    });
    this.debug("round crashed", {
      round: this.currentRoundId,
      multiplier: this.currentMultiplier,
    });

    this.schedule(() => this.beginWaitingRound(), DEMO_CONFIG.crashPauseMs);
  }

  private placeBet(action: DemoAction): void {
    const stake = Number(action.stake);
    const betType = String(action.betType || "1");
    const duplicate = [...this.activeBets.values()].some(
      (bet) => bet.round === this.currentRoundId && bet.betType === betType,
    );

    if (this.stage !== "WAITING") {
      this.emitError("Betting is closed for this round", action);
      return;
    }
    if (
      !Number.isFinite(stake) ||
      stake < DEMO_CONFIG.minBet ||
      stake > DEMO_CONFIG.maxBet
    ) {
      this.emitError("Invalid demo bet amount", action);
      return;
    }
    if (duplicate) {
      this.emitError("A bet already exists for this position", action);
      return;
    }
    if (!this.wallet.debit(stake)) {
      this.emitError("Insufficient demo balance", action);
      return;
    }

    const betId = `demo-bet-${this.currentRoundId}-${betType}-${++this.betSequence}`;
    const bet: ActiveDemoBet = {
      betId,
      betType,
      stake,
      round: this.currentRoundId,
      createdAt: new Date().toISOString(),
    };
    this.activeBets.set(betId, bet);
    this.emit({ type: "balance_update", balance: this.wallet.getBalance() });
    this.schedule(() => {
      if (!this.activeBets.has(betId) || this.stage === "STOPPED") return;
      this.emit({
        type: "3",
        status: "success",
        bets: [this.toBetPayload(bet)],
      });
    }, 80);
    this.debug("bet placed", bet);
  }

  private cancelBet(action: DemoAction): void {
    if (this.stage !== "WAITING") {
      this.emitError("This bet can no longer be cancelled", action);
      return;
    }

    const bet = this.findBet(action);
    if (!bet) {
      this.emitError("Demo bet was not found", action);
      return;
    }

    this.activeBets.delete(bet.betId);
    this.wallet.credit(bet.stake);
    this.emit({ type: "balance_update", balance: this.wallet.getBalance() });
    this.emit({
      type: "7",
      status: "success",
      action: "CancelBet",
      betId: bet.betId,
      betType: bet.betType,
      round: bet.round,
    });
    this.debug("bet cancelled", bet);
  }

  private cashOut(action: DemoAction): void {
    if (this.stage !== "RUNNING") {
      this.emitError("Cash out is only available while flying", action);
      return;
    }

    const bet = this.findBet(action);
    if (!bet) {
      this.emitError("Demo bet is no longer active", action);
      return;
    }

    this.activeBets.delete(bet.betId);
    const payout = Number((bet.stake * this.currentMultiplier).toFixed(2));
    this.wallet.credit(payout);
    this.emit({ type: "balance_update", balance: this.wallet.getBalance() });
    this.emit({
      type: "cashout_success",
      status: "CASHOUT",
      nation: "CASHOUT",
      betId: bet.betId,
      betid: bet.betId,
      betType: bet.betType,
      round: bet.round,
      stake: bet.stake,
      multiplier: this.currentMultiplier,
      RUNValue: this.currentMultiplier,
      cashOut: payout,
      cashout: payout,
    });
    this.debug("cash out", {
      ...bet,
      multiplier: this.currentMultiplier,
      payout,
    });
  }

  private findBet(action: DemoAction): ActiveDemoBet | undefined {
    const requestedId = action.betId || action.betid;
    if (requestedId && this.activeBets.has(requestedId)) {
      return this.activeBets.get(requestedId);
    }

    const betType = String(action.betType || "");
    return [...this.activeBets.values()].find(
      (bet) =>
        bet.round === this.currentRoundId &&
        (!betType || bet.betType === betType),
    );
  }

  private toBetPayload(bet: ActiveDemoBet): DemoBetPayload {
    return {
      betId: bet.betId,
      clientName: "Demo Player",
      stake: bet.stake,
      round: bet.round,
      betType: bet.betType,
      createdAt: bet.createdAt,
      nation: "PLACED",
    };
  }

  private emitError(message: string, action: DemoAction): void {
    this.emit({
      type: "ERROR",
      status: "error",
      message,
      betType: String(action.betType || "1"),
      round: this.currentRoundId,
    });
    this.debug("action rejected", { message, action });
  }

  private emit(message: DemoSocketMessage): void {
    this.listeners.forEach((listener) => listener(message));
  }

  private schedule(callback: () => void, delay: number): void {
    const timer = setTimeout(() => {
      this.timers.delete(timer);
      callback();
    }, delay);
    this.timers.add(timer);
  }

  private clearTimers(): void {
    if (this.waitingInterval) clearInterval(this.waitingInterval);
    if (this.multiplierInterval) clearInterval(this.multiplierInterval);
    this.waitingInterval = null;
    this.multiplierInterval = null;
    this.timers.forEach((timer) => clearTimeout(timer));
    this.timers.clear();
  }

  private seedHistory(): void {
    const now = Date.now();
    this.history = Array.from({ length: 15 }, (_, index) =>
      this.createRoundResult(
        this.currentRoundId - index - 1,
        Number(randomDemoMultiplier()),
        new Date(now - (index + 1) * 12_000).toISOString(),
      ),
    );
  }

  private createRoundResult(
    roundId: number,
    multiplier: number,
    createdAt: string,
  ): DemoRoundResult {
    return {
      roundId: String(roundId),
      multiplier: multiplier.toFixed(2),
      createdAt,
      provablyFair: {
        clientSeed: [
          {
            seed: `demo-client-${roundId}`,
            name: "Demo Player",
            _id: "demo-player",
          },
        ],
        serverSeedHash: "N/A",
        serverSeed: `demo-server-${roundId}`,
        crashPoint: `${multiplier.toFixed(2)}x`,
        proof: "Local demo simulation — no real-money outcome",
      },
    };
  }

  private debug(message: string, data?: unknown): void {
    if (!DEMO_CONFIG.demoDebug) return;
    // eslint-disable-next-line no-console
    console.info(`[demo] ${message}`, data ?? "");
  }
}
